/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Modal component for Mobile Cash
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Misha Kokoulin
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { sumIcon } from '@assets/icons';
import { Button, Input, LinkBlock } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import { EGTMWidgetPersonalAccountCloseType } from '@modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetPersonalAccountOperations';
import type { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type { ICommonTranslation } from '@modules/internationalization/types';
import { makeSelectorModalIsOpen } from '@modules/modal/modalSelectors';
import type { IRootReducer } from '@reducers/rootReducer';

import { changeTableModalValue, loadMobileCash } from '../../actions/tableActions';
import { makeSelectorTableModalValue } from '../../selectors/tableSelectors';
import type { IChangeTableModalValuePayload, ILoadMobileCashPayload } from '../../types/tableTypes';
import {
    FooterModalFooterCloseStyled,
    FooterModalFooterStyled,
    FooterModalStyled,
    FooterModalTextStyled,
    FooterModalTitleStyled,
} from './styled/footerModalStyled';

interface IMobileCashModalConnectedProps {
    modalIsOpen: boolean;
    modalValue: IChangeTableModalValuePayload;
    translation: ICommonTranslation;
}

export interface IMobileCashModalProps {
    changeTableModalValue: (payload: IChangeTableModalValuePayload) => void;
    clientId: string;
    loadMobileCash: (payload: ILoadMobileCashPayload) => void;
    onApplyCallback?: (prepaySum: string) => void;
    onCancelClick?: (type: EGTMWidgetPersonalAccountCloseType) => void;
    onCloseCallback?: (type: EGTMWidgetPersonalAccountCloseType) => void;
    onCloseModal: () => void;
    onErrorLoadCallback?: (error: string) => void;
    onSuccessLoadCallBack?: (prepaySum: string) => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    titleIsHidden?: boolean;
    disableIfNoClientId?: boolean;
    dontCloseOnApply?: boolean;
    closeButtonText?: string;
    applyButtonTooltip?: string;
}

// RegExp only number
const numberRegExp = new RegExp(/^\d+(?:[.,]\d+)?$/);

const checkValue = (value: string): boolean => numberRegExp.test(value) && parseFloat(value) !== 0;

const MobileCashModal: FC<IMobileCashModalProps & IMobileCashModalConnectedProps> = props => {

    const cashValue = props.modalValue.mobileCash?.cashValue ?? '';
    const [inputKind, setInputKind] = useState(EInputKind.normal);
    const [isDisabled, setButtonDisable] = useState(!checkValue(cashValue));

    const isDisabledByClientId = props.disableIfNoClientId && !props.clientId;
    const disableHoverListener = !props.applyButtonTooltip || !isDisabled && !isDisabledByClientId;
    const tooltip = isDisabledByClientId ? (props.applyButtonTooltip || '') : props.translation.modal.summ;

    const onApply = (): void => {
        const value = cashValue.replace(/^[0]*/g, '').replace(/[,]/g, '.');
        props.loadMobileCash({
            amount: value,
            clientId: props.clientId,
            onErrorCallback: props.onErrorLoadCallback,
            onSuccessCallBack: props.onSuccessLoadCallBack,
        });
        props.changeTableModalValue({
            mobileCash: {
                cashValue: '',
            },
        });
        if (!props.dontCloseOnApply) {
            props.onCloseModal();
        }
        setButtonDisable(true);
        props.onApplyCallback?.(value);
    };

    const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const cash = event.target.value;
        props.changeTableModalValue({
            mobileCash: {
                cashValue: cash,
            },
        });
        if (checkValue(cash)) {
            setInputKind(EInputKind.normal);
            setButtonDisable(false);
        } else {
            setInputKind(EInputKind.error);
            setButtonDisable(true);
        }
    };

    const onFooterModalCloseLink = (): void => {
        props.onCloseModal();
        props.onCancelClick?.(EGTMWidgetPersonalAccountCloseType.cancelLink);
    };

    return (
        <FooterModalStyled className="prepayment-invoice"
            data-component={'modal-popup'}
            data-name={'mobile-cash-modal'}>
            {
                !props.titleIsHidden && 
                <FooterModalTitleStyled className="prepayment-invoice__title">
                    {props.translation.modal.prepaymentInvoice}
                </FooterModalTitleStyled>
            }

            <FooterModalTextStyled className="prepayment-invoice__body">
                <Input
                    autoFocus={true}
                    className="mobile-cash-modal__input"
                    kind={inputKind}
                    maxLength={12}
                    placeholder={props.translation.modal.summ}
                    righticon={sumIcon}
                    value={cashValue}
                    onChange={inputChangeHandler}
                    onEnter={onApply}
                />
            </FooterModalTextStyled>

            <FooterModalFooterStyled
                className="prepayment-invoice__buttons"
                justifyContent="flex-start"
            >
                <ArrowTooltip
                    // Tooltip is displayed when the button is locked
                    disableHoverListener={disableHoverListener}
                    key={props.applyButtonTooltip}
                    placement="top"
                    title={tooltip}
                    titleAlign="center"
                >
                    <div>
                        <Button
                            data-modal-attention="close-button"
                            disabled={isDisabled || isDisabledByClientId}
                            kind="mediumNormal"
                            onClick={onApply}
                        >
                            {props.translation.modal.formReport}
                        </Button>
                    </div>
                </ArrowTooltip>
                <FooterModalFooterCloseStyled
                    onClick={onFooterModalCloseLink}
                >
                    <LinkBlock>
                        {props.closeButtonText || props.translation.modal.close}
                    </LinkBlock>
                </FooterModalFooterCloseStyled>
            </FooterModalFooterStyled>
        </FooterModalStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IMobileCashModalConnectedProps>({
    modalIsOpen: makeSelectorModalIsOpen(),
    modalValue: makeSelectorTableModalValue(),
    translation: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    changeTableModalValue: (payload: IChangeTableModalValuePayload) => dispatch(changeTableModalValue(payload)),
    loadMobileCash: (payload: ILoadMobileCashPayload) => dispatch(loadMobileCash(payload)),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MobileCashModal);
